import { Container, Singleton } from 'typescript-ioc/es5'
import aqe from '@pushly/aqe'
import { HttpMethod } from '@pushly/aqe/lib/enums'
import { IApiCallOptions, IApiDeleteResponse, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { AbstractRequestService } from '@pushly/aqe/lib/services/abstract-request-service'
import { PageAnalysisScope } from '@pushly/models/lib/enums'
import { AppService } from './app'
import { ApiVersion } from '../enums/api-version.enum'
import {
    IPageAnalysisContentAgeLimit,
    IPageAnalysisUrlRule,
    PageAnalysisContentAgeLimitDraft,
    PageAnalysisUrlRuleDraft,
} from '../interfaces/page-analysis-gates'

/**
 * The two Enrichment Gates: URL rules and the content age limit. Both decline
 * only the model call - fetch, title/description/image/keywords and catalog
 * extraction still run on a declined page.
 */
@Singleton
export class PageAnalysisGateService extends AbstractRequestService {
    public appService: AppService

    public constructor() {
        super()
        this.appService = Container.get(AppService)
    }

    public async fetchUrlRules(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<IPageAnalysisUrlRule[]>> {
        return this.request(HttpMethod.GET, this.urlRulesEndpoint(domainId, options.version), options)
    }

    public async createUrlRule(
        domainId: number,
        rule: PageAnalysisUrlRuleDraft,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<IPageAnalysisUrlRule>> {
        return this.request(HttpMethod.POST, this.urlRulesEndpoint(domainId, options.version), {
            ...options,
            data: rule,
            responseType: Object as any,
        })
    }

    /**
     * PATCH by verb only. The route validates the body as a whole rule, so every
     * field is required on the way in and a partial is a 400: changing one thing
     * means sending back the rest as it stands.
     */
    public async updateUrlRule(
        domainId: number,
        urlRuleId: number,
        rule: PageAnalysisUrlRuleDraft,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<IPageAnalysisUrlRule>> {
        return this.request(HttpMethod.PATCH, `${this.urlRulesEndpoint(domainId, options.version)}/${urlRuleId}`, {
            ...options,
            data: rule,
            responseType: Object as any,
        })
    }

    public async deleteUrlRule(
        domainId: number,
        urlRuleId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiDeleteResponse> {
        return this.requestDelete(`${this.urlRulesEndpoint(domainId, options.version)}/${urlRuleId}`, options)
    }

    public async fetchContentAgeLimits(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<IPageAnalysisContentAgeLimit[]>> {
        return this.request(HttpMethod.GET, this.ageLimitsEndpoint(domainId, options.version), options)
    }

    /**
     * Addressed by scope, not by id: a domain has at most one live limit per
     * traffic path, so there is nothing else for the caller to identify.
     */
    public async putContentAgeLimit(
        domainId: number,
        scope: PageAnalysisScope,
        limit: PageAnalysisContentAgeLimitDraft,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<IPageAnalysisContentAgeLimit>> {
        return this.request(HttpMethod.PUT, `${this.ageLimitsEndpoint(domainId, options.version)}/${scope}`, {
            ...options,
            data: limit,
            responseType: Object as any,
        })
    }

    /**
     * The version segment already carries its own trailing slash, so an absent
     * version has to resolve to the empty string rather than to `undefined`.
     */
    private base = (domainId: number, version: ApiVersion | undefined) =>
        `${aqe.defaults.publicApiDomain}/${version ?? ApiVersion.NONE}domains/${domainId}/page-analysis`

    private urlRulesEndpoint = (domainId: number, version: ApiVersion | undefined) =>
        `${this.base(domainId, version)}/url-rules`

    private ageLimitsEndpoint = (domainId: number, version: ApiVersion | undefined) =>
        `${this.base(domainId, version)}/content-age-limits`
}
